<template>
  <div>
    <v-container>
      <!-- Date Range Picker -->
      <v-layout row wrap class="mb-4">
        <v-flex md4 sm12 class="pe-2">
          <choose-date
            clearable
            :label="$t('statisticsPage.fromDate')"
            v-model="fromDate"
            @input="onDateChange"
          ></choose-date>
        </v-flex>
        <v-flex md4 sm12 class="ps-2 pe-2">
          <choose-date
            clearable
            :label="$t('statisticsPage.toDate')"
            v-model="toDate"
            @input="onDateChange"
          ></choose-date>
        </v-flex>
        <v-flex md4 sm12 class="ps-2 d-flex align-center justify-end">
          <v-btn
            color="primary"
            @click="fetchData"
            :loading="loading"
            :disabled="!fromDate || !toDate"
          >
            {{ $t("statisticsPage.loadData") }}
          </v-btn>
        </v-flex>
      </v-layout>

      <!-- Loading Indicator -->
      <loading-component :value="loading"></loading-component>

      <!-- Statistics Sections -->
      <div v-if="!loading && dataLoaded">
        <!-- Bookings Section -->
        <v-card class="mb-4">
          <v-card-title>
            <h2>{{ $t("statisticsPage.bookings") }}</h2>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 md4>
                <v-widget :title="$t('statisticsPage.bookings')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatNumber(stats.bookings.total) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md4>
                <v-widget :title="$t('statisticsPage.canceledOrders')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatNumber(stats.bookings.canceled) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md4>
                <v-widget :title="$t('statisticsPage.completedOrders')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatNumber(stats.bookings.completed) }}
                  </div>
                </v-widget>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <!-- Financials Section -->
        <v-card class="mb-4">
          <v-card-title>
            <h2>{{ $t("statisticsPage.financials") }}</h2>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.bookingRevenue')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatCurrency(stats.financials.totalRevenue) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.redeemedVouchers')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatCurrency(stats.financials.redeemedVouchers) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.captainsProfit')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatCurrency(stats.financials.captainsProfit) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.snapProfit')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatCurrency(stats.financials.snapDriveProfit) }}
                  </div>
                </v-widget>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <!-- Commission & ownership breakdown (C2) -->
        <v-card class="mb-4" v-if="stats.financials.driverShare !== undefined">
          <v-card-title><h3>{{ $t("statisticsPage.commissionOwnership") }}</h3></v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.driverShare')">
                  <div slot="widget-content" class="headline text-xs-center" style="color:#2e7d32">
                    {{ formatCurrency(stats.financials.driverShare) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.companyCommission')">
                  <div slot="widget-content" class="headline text-xs-center" style="color:#1565c0">
                    {{ formatCurrency(stats.financials.companyShare) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.carOwnersShareExternal')">
                  <div slot="widget-content" class="headline text-xs-center" style="color:#ef6c00">
                    {{ formatCurrency(stats.financials.ownerShareExternal) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.companyCarsShare')">
                  <div slot="widget-content" class="headline text-xs-center" style="color:#1565c0">
                    {{ formatCurrency(stats.financials.ownerShareCompanyCars) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.ownerShareTotal')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatCurrency(stats.financials.ownerShareTotal) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md3>
                <v-widget :title="$t('statisticsPage.companyTotalRevenue')">
                  <div slot="widget-content" class="headline text-xs-center" style="color:#1565c0; font-weight:bold">
                    {{ formatCurrency(stats.financials.companyTotalRevenue) }}
                  </div>
                </v-widget>
              </v-flex>
            </v-layout>
            <div class="grey--text caption mt-2" style="padding:0 8px">
              {{ $t("statisticsPage.companyRevenueNote") }}
            </div>
          </v-card-text>
        </v-card>

        <!-- Passengers & Drivers Section -->
        <v-card class="mb-4">
          <v-card-title>
            <h2>{{ $t("statisticsPage.passengersDrivers") }}</h2>
          </v-card-title>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 md4>
                <v-widget :title="$t('statisticsPage.newPassengers')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatNumber(stats.passengersAndDrivers.newPassengers) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md4>
                <v-widget :title="$t('statisticsPage.activePassengers')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatNumber(stats.passengersAndDrivers.activePassengers) }}
                  </div>
                </v-widget>
              </v-flex>
              <v-flex xs12 md4>
                <v-widget :title="$t('statisticsPage.activeDrivers')">
                  <div slot="widget-content" class="headline text-xs-center">
                    {{ formatNumber(stats.passengersAndDrivers.activeDrivers) }}
                  </div>
                </v-widget>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </div>

      <!-- Empty State -->
      <v-card v-if="!loading && !dataLoaded" class="text-xs-center pa-4">
        <p class="title">{{ $t("statisticsPage.selectDateRange") }}</p>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { companyNow } from "@/util/companyTime";
import ChooseDate from "@/components/monaco/input/ChooseDate";
import LoadingComponent from "@/components/LoadingComponent";
import VWidget from "@/components/VWidget";

export default {
  name: "AnalyticsSummary",
  title() {
    return this.$t("statisticsPage.analyticsSummaryTitle");
  },
  components: {
    ChooseDate,
    LoadingComponent,
    "v-widget": VWidget
  },
  data() {
    const today = companyNow().format("YYYY-MM-DD");
    return {
      fromDate: today,
      toDate: today,
      loading: false,
      dataLoaded: false,
      stats: {
        bookings: {
          total: 0,
          canceled: 0,
          completed: 0
        },
        financials: {
          totalRevenue: 0,
          redeemedVouchers: 0,
          captainsProfit: 0,
          snapDriveProfit: 0
        },
        passengersAndDrivers: {
          newPassengers: 0,
          activePassengers: 0,
          activeDrivers: 0
        }
      }
    };
  },
  methods: {
    fetchData() {
      if (!this.fromDate || !this.toDate) {
        window.getApp.$emit("SHOW_TOAST", this.$t("statisticsPage.selectBothDates"), "red");
        return;
      }

      // Validate date range
      if (moment(this.fromDate).isAfter(moment(this.toDate))) {
        window.getApp.$emit("SHOW_TOAST", this.$t("statisticsPage.fromBeforeTo"), "red");
        return;
      }

      this.loading = true;
      this.dataLoaded = false;

      const params = {
        from: moment(this.fromDate).format("YYYY-MM-DD"),
        to: moment(this.toDate).format("YYYY-MM-DD")
      };

      // TODO: Replace with actual API endpoint when backend is ready
      // For now, using dummy data structure
      axios
        .get("analytics/summary", { params })
        .then((res) => {
          if (res.data && res.data.data) {
            this.stats = res.data.data;
            this.dataLoaded = true;
          } else {
            // Fallback to dummy data for frontend testing
            this.loadDummyData();
          }
        })
        .catch((err) => {
          console.error("Error fetching analytics:", err);
          // For now, load dummy data if API fails
          this.loadDummyData();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadDummyData() {
      // Dummy data for frontend testing until backend is ready
      this.stats = {
        bookings: {
          total: 1250,
          canceled: 85,
          completed: 1165
        },
        financials: {
          totalRevenue: 1250000,
          redeemedVouchers: 50000,
          captainsProfit: 750000,
          snapDriveProfit: 450000
        },
        passengersAndDrivers: {
          newPassengers: 150,
          activePassengers: 320,
          activeDrivers: 45,
          avgOrdersPerDriver: 25.89
        }
      };
      this.dataLoaded = true;
    },
    formatNumber(value, decimals = 0) {
      if (value === null || value === undefined) return "0";
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return `${this.$t("statisticsPage.currency")} 0`;
      return `${this.$t("statisticsPage.currency")} ${this.formatMoney(value)}`;
    },
    onDateChange() {
      // Auto-fetch when dates change (optional - can be removed if not needed)
      // This will trigger fetch when user changes dates
    }
  },
  mounted() {
    // Auto-load data for today when page loads
    if (this.fromDate && this.toDate) {
      this.fetchData();
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 24px;
}
</style>
